"use client";

import { useEffect, useState } from "react";
import { jobEventSource } from "../lib/api";
import type { JobStatus } from "../lib/types";

export interface JobProgressProps {
  jobId: string;
  onCompleted?: () => void;
  onFailed?: (msg: string) => void;
}

interface ProgressData {
  percent?: number;
  stage?: string;
}

interface StatusData {
  status: JobStatus;
  progressPercent?: number;
  progressStage?: string;
}

export function JobProgress({ jobId, onCompleted, onFailed }: JobProgressProps) {
  const [status, setStatus] = useState<JobStatus>("queued");
  const [progress, setProgress] = useState<ProgressData>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setStatus("queued");
    setProgress({});
    setError(null);

    const es = jobEventSource(jobId);

    es.addEventListener("status", (evt: MessageEvent) => {
      try {
        const data = JSON.parse(evt.data) as StatusData;
        setStatus(data.status);
        if (data.progressPercent !== undefined || data.progressStage) {
          setProgress({
            percent: data.progressPercent,
            stage: data.progressStage,
          });
        }
        if (data.status === "completed") onCompleted?.();
        if (data.status === "failed" || data.status === "cancelled") {
          onFailed?.(data.status);
        }
      } catch (e) {
        console.warn("[job-progress] status parse error", e);
      }
    });

    es.addEventListener("progress", (evt: MessageEvent) => {
      try {
        const data = JSON.parse(evt.data) as ProgressData;
        setProgress(data);
      } catch (e) {
        console.warn("[job-progress] progress parse error", e);
      }
    });

    es.addEventListener("error", (evt: MessageEvent) => {
      try {
        const data = JSON.parse(evt.data) as { message?: string };
        if (data.message) {
          setError(data.message);
          onFailed?.(data.message);
        }
      } catch {
        // EventSource fires generic 'error' events too; ignore those.
      }
    });

    es.addEventListener("done", () => {
      es.close();
    });

    return () => {
      es.close();
    };
  }, [jobId, onCompleted, onFailed]);

  const percent = progress.percent ?? 0;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium capitalize">{status}</span>
        {progress.stage && (
          <span className="text-muted-foreground">{progress.stage}</span>
        )}
      </div>
      <div className="h-2 w-full overflow-hidden rounded bg-muted">
        <div
          className="h-full bg-emerald-500 transition-all"
          style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
        />
      </div>
      {error && (
        <p className="text-xs text-red-500">{error}</p>
      )}
    </div>
  );
}
